import { useEffect, useMemo, useState } from 'react';
import ConfirmModal from '../../components/modal/confirm-modal';
import ToastNotification, { showToast } from '../../components/toasts/ToastNotification';
import './admin-employee.css';
import { HashLoader } from 'react-spinners';
import { deleteUserForAdmin, getEmployees } from '../../api/user.api';
import { hasPermission } from '../../utils/auth';
import { useNavigate } from 'react-router-dom';
import { FaEye } from "react-icons/fa6";
import { MdDelete, MdOutlineAdd } from "react-icons/md";
import { useUser } from '../../context/UserContext';
import AddEmployeeModal from '../../components/modal/add-employee-modal';

export const AdminEmployee = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    document.title = 'Admin - Nhân viên';
    fetchEmployees();
  }, []);

  const summary = useMemo(() => {
    return employees.reduce(
      (acc, item) => {
        if (item.role === 'admin') acc.admin += 1;
        if (item.role === 'editor') acc.editor += 1;
        if (item.role === 'supporter') acc.supporter += 1;
        return acc;
      },
      { admin: 0, editor: 0, supporter: 0 }
    );
  }, [employees]);

  const fetchEmployees = async (showLoader = true) => {
    if (showLoader) setIsFetching(true);
    try {
      const res = await getEmployees();

      if (res.success) {
        setEmployees(res.data.employees || []);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const handleDeleteUser = async () => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setIsDeleteModalOpen(false);
    try {
      const res = await deleteUserForAdmin(selectedIdToDelete);
      if (res.success) {
        await fetchEmployees(false);
        showToast("Xóa nhân viên thành công", "success");
        setSelectedIdToDelete("");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="admin-employee-page">
      <AddEmployeeModal
        title={'Thêm một nhân viên mới'}
        isOpen={isAddEmployeeModalOpen}
        onClose={() => setIsAddEmployeeModalOpen(false)}
        setEmployees={setEmployees}
      />

      <div className="admin-employee-shell">
        <div className="admin-employee-header">
          <div>
            <p className="admin-employee-breadcrumb">Quản trị / Nhân sự</p>
            <h1 className="admin-employee-title">Danh sách nhân viên</h1>
          </div>
          <div className="admin-employee-kpi">
            <div className="admin-employee-kpi-item">
              <span>Tổng nhân sự</span>
              <strong>{employees.length}</strong>
            </div>
            <div className="admin-employee-kpi-item">
              <span>Admin</span>
              <strong>{summary.admin}</strong>
            </div>
            <div className="admin-employee-kpi-item">
              <span>Editor/Support</span>
              <strong>{summary.editor + summary.supporter}</strong>
            </div>
          </div>
        </div>

        {hasPermission(user?.role, "create:employees") && (
          <div className="admin-employee-toolbar">
            <button
              type="button"
              className="admin-employee-add-button"
              onClick={() => setIsAddEmployeeModalOpen(true)}
            >
              <MdOutlineAdd className="admin-employee-add-icon" />
              <span>Thêm nhân viên mới</span>
            </button>
          </div>
        )}

        {isFetching && employees.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <div className="admin-employee-table-wrap">
            <table className="admin-employee-table">
              <thead>
                <tr>
                  <th>STT</th>
                  <th>ID</th>
                  <th>Tên người dùng</th>
                  <th>Vai trò</th>
                  <th>Tạo lúc</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {employees.length > 0 ? (
                  employees.map((item, index) => (
                    <tr key={item?._id}>
                      <td className="admin-employee-cell-muted">{index + 1}</td>
                      <td className="admin-employee-cell-code">{item?._id}</td>
                      <td>
                        <button
                          type="button"
                          disabled={item?._id === user?._id}
                          className="admin-employee-name-link"
                          onClick={() => navigate(`/super-admin/users/view/${item?._id}`)}
                        >
                          {item?.username}
                        </button>
                      </td>
                      <td>
                        <span className={`admin-employee-role admin-employee-role-${item?.role}`}>{item?.role}</span>
                      </td>
                      <td className="admin-employee-cell-muted">{new Date(item?.createdAt).toLocaleString("vi-VN")}</td>
                      <td>
                        <div className="admin-employee-actions">
                          {item?._id !== user?._id && (
                            <>
                              <button
                                onClick={() => navigate(`/super-admin/users/view/${item?._id}`)}
                                className="admin-employee-action admin-employee-action-view"
                                disabled={isActionLoading}
                                title="Xem chi tiết"
                              >
                                <FaEye />
                              </button>
                              {hasPermission(user?.role, "delete:user") && (
                                <button
                                  onClick={() => {
                                    setIsDeleteModalOpen(true);
                                    setSelectedIdToDelete(item?._id);
                                  }}
                                  className="admin-employee-action admin-employee-action-delete"
                                  disabled={isActionLoading}
                                  title="Xóa nhân viên"
                                >
                                  <MdDelete />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <p className="admin-employee-empty">Không có nhân viên.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ToastNotification />
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteUser}
        onClose={() => setIsDeleteModalOpen(false)}
        message={"Hãy chắc chắn rằng bạn đang xóa nhân viên này, điều này sẽ xóa tất cả dữ liệu liên quan."}
        title={"Xóa nhân viên"}
      />
    </div>
  );
};
